/**
 * Le scadenze in arrivo: aggiornamenti degli obblighi e documenti che scadono.
 *
 * Due tabelle, due domande diverse
 * --------------------------------
 * * **Aggiornamento periodico degli obblighi** — ogni quanto un obbligo va
 *   rinnovato. E' un dato dell'obbligo, e dove il Comune non l'ha dichiarato
 *   e' un'ipotesi ereditata dalla sezione: la riga lo dice.
 * * **Documenti in scadenza** — due date diverse del singolo file: la fine
 *   della pubblicazione (poi il file passa nell'archivio della pagina, e resta
 *   accessibile) e la scadenza del file (poi va sostituito, ma resta
 *   pubblicato). Stanno nella stessa tabella perche' riguardano lo stesso
 *   oggetto, con una colonna che dice quale delle due scade.
 *
 * Qui c'e' solo cio' che **deve ancora** scadere. Cio' che e' gia' scaduto sta
 * fra le allerte, nella stessa pagina, con i gesti per rimediare.
 */

import type { Documento, IdNodo } from "../api/types";
import { partiPercorso, SEPARATORE_PERCORSO } from "../lib/alberatura";
import type { CatenePerId } from "../lib/alberatura";
import {
  cadenzaRisoltaDi,
  ETICHETTE_CADENZA,
  ETICHETTE_TIPO_SCADENZA,
  GIORNI_PREAVVISO,
  testoDurata,
} from "../lib/scadenze";
import type {
  CadenzaRisolta,
  ScadenzaAggiornamento,
  ScadenzaArchiviazione,
  ScadenzaValidita,
} from "../lib/scadenze";
import { Bottone, Chip, ChipNodo, Scheda, Scheletro, StatoVuoto } from "./primitive";
import { ProvenienzaCadenza } from "./ProvenienzaCadenza";

/** Chip dei giorni che mancano: giallo nella finestra di preavviso. */
function ChipGiorni({ giorni }: { giorni: number }) {
  if (giorni === 0) return <Chip tono="attenzione">oggi</Chip>;
  if (giorni <= GIORNI_PREAVVISO) return <Chip tono="attenzione">fra {giorni} gg</Chip>;
  return <Chip tono="neutro">fra {giorni} gg</Chip>;
}

/** Chip «da confermare» accanto a una frequenza che il Comune non ha dichiarato. */
function ChipDaConfermare({ risolta }: { risolta: CadenzaRisolta }) {
  if (risolta.fonte === "dichiarata") return null;
  return (
    <Chip tono="neutro" pallino={false}>
      da confermare
    </Chip>
  );
}

/** Il percorso dell'obbligo, con il titolo della pubblicazione sotto. */
function PercorsoObbligo({
  catene,
  scadenza,
}: {
  catene: CatenePerId;
  scadenza: ScadenzaAggiornamento | ScadenzaArchiviazione | ScadenzaValidita;
}) {
  return (
    <>
      <ChipNodo {...partiPercorso(catene, scadenza.id_nodo)} separatore={SEPARATORE_PERCORSO} />
      {scadenza.pubblicazione ? (
        <>
          <br />
          <span className="campo__aiuto">{scadenza.pubblicazione.titolo}</span>
        </>
      ) : null}
    </>
  );
}

export function TabelleScadenze({
  aggiornamenti,
  documenti,
  etichettaOrizzonte,
  inAttesa,
  catene,
  documentoPerId,
  eSuperAdmin,
  regolePronte,
  titoloNonPronte,
  onRegole,
  onRegoleDaDocumento,
  onDurata,
  onScadenza,
}: {
  aggiornamenti: readonly ScadenzaAggiornamento[];
  documenti: readonly (ScadenzaArchiviazione | ScadenzaValidita)[];
  etichettaOrizzonte: string;
  inAttesa: boolean;
  catene: CatenePerId;
  /** Il documento completo, per le modali: la riga ne porta solo id e titolo. */
  documentoPerId: ReadonlyMap<number, Documento>;
  eSuperAdmin: boolean;
  regolePronte: boolean;
  titoloNonPronte: string;
  onRegole: (idNodo: IdNodo, denominazione: string, corrente: CadenzaRisolta) => void;
  onRegoleDaDocumento: (scadenza: ScadenzaArchiviazione) => void;
  onDurata: (documento: Documento, idNodo: IdNodo) => void;
  onScadenza: (documento: Documento, idNodo: IdNodo) => void;
}) {
  const bottoneRegole = (onClick: () => void, denominazione: string) => (
    <Bottone
      variante="fantasma"
      dimensione="piccolo"
      disabled={!regolePronte}
      title={regolePronte ? undefined : titoloNonPronte}
      onClick={onClick}
      aria-label={`Regole dell'obbligo ${denominazione}`}
    >
      Regole dell'obbligo
    </Bottone>
  );

  const attesa = (
    <div style={{ padding: "1.25rem" }}>
      <Scheletro righe={4} />
    </div>
  );

  return (
    <>
      <Scheda
        titolo={`Obblighi da aggiornare entro ${etichettaOrizzonte} (${aggiornamenti.length})`}
        descrizione="Secondo la frequenza di aggiornamento, dall'ultimo aggiornamento."
        fitto
      >
        {inAttesa ? (
          attesa
        ) : aggiornamenti.length === 0 ? (
          <StatoVuoto
            titolo="Nessun aggiornamento in arrivo"
            testo="Allarghi l'orizzonte per vedere le scadenze piu' lontane."
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <caption className="visually-hidden">
                {aggiornamenti.length} obblighi da aggiornare, dal piu' vicino.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Obbligo</th>
                  <th scope="col">Frequenza</th>
                  <th scope="col">Scadenza</th>
                  {eSuperAdmin ? (
                    <th scope="col">
                      <span className="tabella__azioni">Azioni</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {aggiornamenti.map((scadenza) => {
                  const risolta = cadenzaRisoltaDi(scadenza);
                  return (
                    <tr key={`${scadenza.id_nodo}-aggiornamento`}>
                      <td className="tabella__principale">
                        <PercorsoObbligo catene={catene} scadenza={scadenza} />
                      </td>
                      <td className="testo-tenue">
                        {ETICHETTE_CADENZA[scadenza.cadenza]} <ChipDaConfermare risolta={risolta} />
                        <ProvenienzaCadenza
                          fonte={scadenza.fonte_cadenza}
                          idNodoFonte={scadenza.id_nodo_cadenza}
                        />
                      </td>
                      <td>
                        <ChipGiorni giorni={scadenza.giorni_residui} />
                      </td>
                      {eSuperAdmin ? (
                        <td className="tabella__azioni">
                          {bottoneRegole(
                            () => onRegole(scadenza.id_nodo, scadenza.denominazione_nodo, risolta),
                            scadenza.denominazione_nodo,
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>

      <Scheda
        titolo={`Documenti in scadenza entro ${etichettaOrizzonte} (${documenti.length})`}
        descrizione="Fine della pubblicazione: il file passa nell'archivio e resta accessibile. Scadenza del file: va sostituito, e resta pubblicato."
        fitto
      >
        {inAttesa ? (
          attesa
        ) : documenti.length === 0 ? (
          <StatoVuoto
            titolo="Nessun documento in scadenza"
            testo="Allarghi l'orizzonte per vedere le date piu' lontane."
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <caption className="visually-hidden">
                {documenti.length} documenti in scadenza, dal piu' vicino.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Documento</th>
                  <th scope="col">Che cosa scade</th>
                  <th scope="col">Il giorno</th>
                  <th scope="col">
                    <span className="tabella__azioni">Azioni</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {documenti.map((scadenza) => {
                  const chiave = `${scadenza.tipo}-${scadenza.pubblicazione?.id}-${scadenza.documento?.id ?? "intera"}`;
                  const documento = scadenza.documento
                    ? documentoPerId.get(scadenza.documento.id)
                    : undefined;
                  const validita = scadenza.tipo === "validita";
                  return (
                    <tr key={chiave}>
                      <td className="tabella__principale">
                        <span className="documento-titolo">
                          {scadenza.documento?.titolo ??
                            `${scadenza.pubblicazione?.titolo ?? "Contenuto"} (senza documenti)`}
                        </span>
                        <PercorsoObbligo catene={catene} scadenza={scadenza} />
                      </td>
                      <td className="testo-tenue">
                        {ETICHETTE_TIPO_SCADENZA[scadenza.tipo]}
                        <div className="campo__aiuto">
                          {validita
                            ? scadenza.in_correzione
                              ? "sostituto in bozza, da pubblicare"
                              : "da sostituire, resta pubblicato"
                            : `dal ${scadenza.pubblicato_dal ?? "—"}, ${
                                scadenza.durata ? testoDurata(scadenza.durata) : "durata dell'obbligo"
                              }`}
                        </div>
                      </td>
                      <td>
                        <span className="dato">
                          {validita ? scadenza.valido_fino_al : scadenza.fine}
                        </span>
                        <div>
                          <ChipGiorni giorni={scadenza.giorni_residui} />
                        </div>
                      </td>
                      <td className="tabella__azioni">
                        <div className="riga">
                          {documento && validita && !scadenza.in_correzione ? (
                            <Bottone
                              variante="contorno"
                              dimensione="piccolo"
                              onClick={() => onScadenza(documento, scadenza.id_nodo)}
                              aria-label={`Cambia la scadenza di «${documento.titolo}»`}
                            >
                              Scadenza…
                            </Bottone>
                          ) : null}
                          {documento && !validita ? (
                            <Bottone
                              variante="contorno"
                              dimensione="piccolo"
                              onClick={() => onDurata(documento, scadenza.id_nodo)}
                              aria-label={`Cambia la durata di «${documento.titolo}»`}
                            >
                              Durata…
                            </Bottone>
                          ) : null}
                          {!validita && scadenza.documento && scadenza.documento.url === null ? (
                            // Una correzione in attesa: la fine della pubblicazione
                            // e' quella del documento online, che resta tale
                            // finche' la correzione non e' pubblicata.
                            <span className="campo__aiuto">correzione in bozza</span>
                          ) : null}
                          {eSuperAdmin && !validita
                            ? bottoneRegole(
                                () => onRegoleDaDocumento(scadenza),
                                scadenza.denominazione_nodo,
                              )
                            : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>
    </>
  );
}
